import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeSlug, setActiveSlug] = useState(slug || 'hot');

  const filteredComics = useMemo(() => {
    if (activeSlug === 'hot') {
      return [...comics].sort((a, b) => b.rating - a.rating);
    }
    if (activeSlug === 'latest') {
      return [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    return comics.filter((c) => c.categorySlug === activeSlug);
  }, [activeSlug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://www.weibamanhua.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": currentCategory.name,
        "item": `https://www.weibamanhua.com/category/${currentCategory.slug}`
      }
    ]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${currentCategory.name} - 免费韩国漫画在线阅读 - 尾巴漫画网`,
    "description": `尾巴漫画网${currentCategory.name}类韩漫专区，海量精选${currentCategory.name}韩国漫画免费在线阅读。`,
    "url": `https://www.weibamanhua.com/category/${currentCategory.slug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "尾巴漫画网",
      "url": "https://www.weibamanhua.com/"
    }
  };

  return (
    <>
      <SchemaOrg schema={webPageSchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <Navbar />
      <main className="pt-20 pb-14 min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <span>/</span>
            <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {activeSlug === 'hot' ? '精选高分热门韩漫，大家都在看' :
               activeSlug === 'latest' ? '最新上架的韩漫作品，新鲜出炉' :
               `精选${currentCategory.name}类韩漫，精彩不容错过`}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-book-open-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}